// Site-wide translation engine using the /api/translate endpoint (LLM + Mongo cache).
// Walks visible text nodes, translates via backend, applies in place, and re-applies on
// DOM changes (React re-renders, route changes, portals) via a debounced MutationObserver.
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "CODE", "TEXTAREA", "INPUT", "SVG", "PATH"]);

let currentLang = "en";
let observer = null;
let debounceTimer = null;
let translating = false;
const originalByNode = new WeakMap();
let cache = {}; // original(trimmed) -> translated, for currentLang

function loadCache(lang) {
  try { cache = JSON.parse(localStorage.getItem("wt_tr_" + lang) || "{}"); } catch { cache = {}; }
}
function saveCache(lang) {
  try { localStorage.setItem("wt_tr_" + lang, JSON.stringify(cache)); } catch { /* quota */ }
}

function acceptNode(node) {
  const p = node.parentElement;
  if (!p) return false;
  if (SKIP_TAGS.has(p.tagName)) return false;
  if (p.closest("[data-no-translate]")) return false;
  const t = node.nodeValue;
  if (!t || !t.trim()) return false;
  if (!/[A-Za-z]/.test(t)) return false; // skip pure numbers / symbols
  return true;
}

function collect() {
  const nodes = [];
  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, {
    acceptNode: (n) => (acceptNode(n) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT),
  });
  let n;
  while ((n = walker.nextNode())) nodes.push(n);
  return nodes;
}

function apply(node, translated) {
  const orig = originalByNode.get(node) ?? node.nodeValue;
  const lead = orig.match(/^\s*/)[0];
  const trail = orig.match(/\s*$/)[0];
  const val = lead + translated + trail;
  if (node.nodeValue !== val) node.nodeValue = val; // idempotent — avoids observer loops
}

async function fetchTranslations(texts, lang) {
  try {
    const res = await fetch(`${BACKEND}/api/translate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ texts, target: lang }),
    });
    if (!res.ok) return {};
    const data = await res.json();
    return data.translations || {};
  } catch {
    return {};
  }
}

async function runTranslate() {
  if (currentLang === "en" || translating) return;
  translating = true;
  try {
    const nodes = collect();
    const missNodes = []; // nodes needing translation
    const missKeys = [];  // unique keys to fetch
    const seen = new Set();
    for (const node of nodes) {
      let orig = originalByNode.get(node);
      if (orig == null) { orig = node.nodeValue; originalByNode.set(node, orig); }
      const key = orig.trim();
      if (cache[key] != null) { apply(node, cache[key]); continue; }
      missNodes.push(node);
      if (!seen.has(key)) { seen.add(key); missKeys.push(key); }
    }
    if (!missKeys.length) return;

    const applyMap = (map) => {
      Object.assign(cache, map);
      for (const node of missNodes) {
        const key = (originalByNode.get(node) || "").trim();
        if (cache[key] != null) apply(node, cache[key]);
      }
    };

    // Fetch in small client-side chunks and apply progressively.
    const CHUNK = 30;
    for (let i = 0; i < missKeys.length; i += CHUNK) {
      const slice = missKeys.slice(i, i + CHUNK);
      const map = await fetchTranslations(slice, currentLang);
      if (map && Object.keys(map).length) { applyMap(map); saveCache(currentLang); }
    }
  } finally {
    translating = false;
  }
}

function scheduleTranslate() {
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(runTranslate, 220);
}

function startObserver() {
  if (observer) return;
  observer = new MutationObserver(() => { if (currentLang !== "en") scheduleTranslate(); });
  observer.observe(document.body, { childList: true, subtree: true, characterData: true });
}
function stopObserver() {
  if (observer) { observer.disconnect(); observer = null; }
}

export async function setLanguage(lang) {
  const next = lang || "en";
  localStorage.setItem("wt_lang", next);
  if (next === "en") {
    // Restoring originals reliably across a live-translated SPA is cleanest via reload.
    stopObserver();
    document.documentElement.lang = "en";
    currentLang = "en";
    window.location.reload();
    return;
  }
  currentLang = next;
  document.documentElement.lang = next;
  loadCache(next);
  startObserver();
  await runTranslate();
}

export function initLanguage() {
  const saved = localStorage.getItem("wt_lang") || "en";
  if (saved !== "en") {
    currentLang = saved;
    document.documentElement.lang = saved;
    loadCache(saved);
    startObserver();
    setTimeout(runTranslate, 350);
  }
  return saved;
}

export function getLanguage() {
  return localStorage.getItem("wt_lang") || "en";
}

// Re-run after client-side route changes (new page content).
export function translateOnRouteChange() {
  if (getLanguage() !== "en") setTimeout(runTranslate, 250);
}
